import { FIELD_PLACEHOLDER_CHIP_CLASS, splitFieldTokens } from "./quoteFields";

/**
 * How a field token resolves for rendering: the filled value, the label of an
 * unfilled field (shown as a placeholder chip), or a live `<fp-field>` element
 * bound to the source (used when converting markdown to document HTML).
 */
export type FieldTokenResolution =
  | { readonly value: string; readonly title?: string }
  | { readonly placeholderLabel: string }
  | { readonly fieldSource: string };

/** Resolves a token source to its rendering. */
export type FieldTokenResolver = (source: string) => FieldTokenResolution;

/** Minimal mdast node shape this transform reads and rewrites. */
interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
  data?: {
    hName?: string;
    hProperties?: Record<string, unknown>;
    hChildren?: { type: "text"; value: string }[];
  };
}

/**
 * remark plugin that rewrites inline `{{ source }}` field tokens in the parsed
 * markdown: a filled token becomes a literal text run, an unfilled one becomes
 * an orange placeholder `span` chip (matching the inline `<fp-field>` look). The
 * value is injected as text, never re-parsed as markdown.
 * @param resolve - Resolves a token source to its rendering
 * @returns The remark transformer
 */
export function remarkFieldTokens(resolve: FieldTokenResolver) {
  return () => (tree: MdNode) => rewrite(tree, resolve);
}

/**
 * Recursively rewrites token-bearing text nodes into text and chip nodes.
 * @param node - The mdast node whose children are rewritten in place
 * @param resolve - Resolves a token source to its rendering
 */
function rewrite(node: MdNode, resolve: FieldTokenResolver): void {
  if (!node.children) {
    return;
  }
  node.children = node.children.flatMap((child): MdNode[] => {
    if (
      child.type === "text" &&
      typeof child.value === "string" &&
      child.value.includes("{{")
    ) {
      return tokenNodes(child.value, resolve);
    }
    rewrite(child, resolve);
    return [child];
  });
}

/**
 * Builds the replacement nodes for one token-bearing text run.
 * @param text - The text run to split
 * @param resolve - Resolves a token source to its rendering
 * @returns The text and chip nodes, in order
 */
function tokenNodes(text: string, resolve: FieldTokenResolver): MdNode[] {
  return splitFieldTokens(text).map((segment): MdNode => {
    if ("text" in segment) {
      return { type: "text", value: segment.text };
    }
    const resolved = resolve(segment.source);
    if ("value" in resolved) {
      return resolved.title === undefined
        ? { type: "text", value: resolved.value }
        : titledValue(resolved.value, resolved.title);
    }
    if ("fieldSource" in resolved) {
      return fieldElement(resolved.fieldSource);
    }
    return placeholderChip(resolved.placeholderLabel);
  });
}

/**
 * Builds the mdast node that renders a filled token as a span carrying the
 * field's name as a hover tooltip.
 * @param value - The resolved field value
 * @param title - The field's friendly label
 * @returns The titled span node
 */
function titledValue(value: string, title: string): MdNode {
  return {
    type: "titledFieldValue",
    data: {
      hName: "span",
      hProperties: { dataFieldTitle: title },
      hChildren: [{ type: "text", value }],
    },
  };
}

/**
 * Builds the mdast node that renders a token as a live `<fp-field>` element.
 * @param source - The validated quote-scope field source
 * @returns The fp-field element node
 */
function fieldElement(source: string): MdNode {
  return {
    type: "fpFieldElement",
    data: { hName: "fp-field", hProperties: { source } },
  };
}

/**
 * Builds the mdast node that renders an unfilled field as an orange chip.
 * @param label - The field's friendly label
 * @returns The placeholder chip node
 */
function placeholderChip(label: string): MdNode {
  return {
    type: "fieldPlaceholder",
    data: {
      hName: "span",
      hProperties: { className: FIELD_PLACEHOLDER_CHIP_CLASS.split(" ") },
      hChildren: [{ type: "text", value: `⟨${label}⟩` }],
    },
  };
}
